const DEFAULT_BASE_ENDPOINT = 'https://cdn.privacy-mgmt.com';

/**
 * Helper function to retrieve the consentUUID from cookies.
 *
 * @returns The consentUUID string if found, otherwise null.
 */
const getConsentUUID = (): string | null => {
    const match = document.cookie.match(/(?:^|;\s*)consentUUID=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Parameters for the setupUtiqSyncSourcepoint function.
 */
export interface DeleteCustomConsentParams {
    baseEndpoint?: string;
    propertyId: string;
    vendors?: string[];
    categories?: string[];
    legIntCategories?: string[];
}

/**
 * Delete custom consent data for specified vendors and categories.
 *
 * @param arg0 - Object containing siteId, and optional arrays of vendors, categories, and legIntCategories to delete consent for.
 */
export const deleteSourcepointCustomConsent = async ({ baseEndpoint = DEFAULT_BASE_ENDPOINT, propertyId, vendors = [], categories = [], legIntCategories = [] }: DeleteCustomConsentParams): Promise<void> => {
    const consentUUID = getConsentUUID();
    if (!consentUUID) throw new Error('consentUUID cookie not found');

    const url = `${baseEndpoint}/consent/tcfv2/consent/v3/custom/${encodeURIComponent(propertyId)}?consentUUID=${encodeURIComponent(consentUUID)}`;

    const response = await fetch(url, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            vendors,
            categories,
            legIntCategories,
        }),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Request failed: ${response.status} ${response.statusText}\n${errorText}`);
    }

    await response.json().catch(() => ({}));
};

/**
 * Parameters for the getSourcepointConsents function.
 */
export interface GetConsentsParams {
    baseEndpoint?: string;
    propertyId: string;
}

/**
 * Result type for the getSourcepointConsents function.
 */
export interface GetConsentsResult {
    /** Consent record identifier (includes a suffix in the example). */
    consentUUID: string;
    /** Granted categories/purposes (explicit consent). */
    categories: string[];
    /** Categories/purposes granted under legitimate interest. */
    legIntCategories: string[];
    /** Vendors included in the consent. */
    vendors: string[];
    /** Vendors processed under legitimate interest. */
    legIntVendors: string[];
    /** Special features (TCF special features). */
    specialFeatures: string[];
    /** IAB TCF consent string. */
    euconsent: string;
    /**
     * Per-vendor grants keyed by vendor id.
     * Each vendor has a boolean grant plus per-purpose grants.
     */
    grants: {
        [vendorId: string]: {
            vendorGrant: boolean;
            purposeGrants: {
                [purposeId: string]: boolean;
            },
        },
    },
    /** ISO timestamp when the record was created. */
    dateCreated: string;
    /** Backend flag indicating presence of consent data. */
    hasConsentData: boolean;
};

/**
 * Function to get purpose and vendor consent status from Sourcepoint CMP.
 * Provided by Sourcepoint team.
 *
 * @param param0 - Object containing propertyId.
 * @returns An object with vendorConsent and purposeConsent booleans or undefined.
 */
export const getSourcepointConsents = async ({ baseEndpoint = DEFAULT_BASE_ENDPOINT, propertyId }: GetConsentsParams): Promise<GetConsentsResult> => {
    const consentUUID = getConsentUUID();
    if (!consentUUID) throw new Error('Consent UUID cookie not found');

    const url = `${baseEndpoint}/consent/tcfv2/consent/v3/${encodeURIComponent(propertyId)}?consentUUID=${consentUUID}&separateLegIntVendors=true`;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Request failed: ${response.status} ${response.statusText}\n${errorText}`);
    }

    const result = await response.json().catch(() => ({}));

    return result;
};
